import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Download, Inbox, Layers, Loader2, Bot, Database, HelpCircle, FileDown, ExternalLink } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import StatusBadge from "./StatusBadge";
import priceRequestsApi from "../../services/priceRequestsApi";

const REFRESH_INTERVAL_MS = 5000;
const MAX_AUTO_REFRESHES = 24; // ≈2 minutes, then stop nagging the server

const BLEXBOT_FILE_BASE = "https://app.blexbot.com/file";

const buildPdfUrl = (r) => {
  if (r?.pdf_url) return r.pdf_url;
  if (r?.pdf) return r.pdf;
  if (r?.quotation_pdf) return r.quotation_pdf;
  if (r?.quote_pdf_id) return `${BLEXBOT_FILE_BASE}/${r.quote_pdf_id}.pdf`;
  return "";
};

const SOURCE_META = {
  db: { label: "Matched database", Icon: Database, className: "text-cyan-700" },
  llm: { label: "AI research", Icon: Bot, className: "text-purple-700" },
  none: { label: "Not researched", Icon: HelpCircle, className: "text-slate-500" },
};

const ProductBreakdown = ({ items = [], currency = "USD" }) => {
  if (!items.length) return <p className="text-sm text-slate-500">No product details.</p>;
  return (
    <table className="w-full text-xs" data-testid="pr-history-breakdown">
      <thead>
        <tr className="text-left uppercase tracking-wide text-slate-500 border-b border-slate-200">
          <th className="py-2 pr-3">Product</th>
          <th className="py-2 pr-3">Qty</th>
          <th className="py-2 pr-3">Source</th>
          <th className="py-2 pr-3 text-right">Unit price</th>
          <th className="py-2 pr-3 text-right">Line total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((p, idx) => {
          const meta = SOURCE_META[p.research_source] || SOURCE_META.none;
          const Icon = meta.Icon;
          return (
            <tr key={idx} className="border-b border-slate-100 last:border-0 align-top">
              <td className="py-2 pr-3 text-slate-800">
                <div className="font-semibold text-[#1E3A5F]">
                  {p.description || "—"}
                </div>
                <div className="text-slate-500">
                  {[p.brand, p.model, p.manufacturer].filter(Boolean).join(" · ") || ""}
                </div>
                {p.research_notes && (
                  <div className="text-slate-500 italic mt-1">{p.research_notes}</div>
                )}
                {p.research_source_url && (
                  <a
                    href={p.research_source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 underline"
                    data-testid="pr-history-source-link"
                    title="Reference URL suggested by AI research"
                  >
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    View source
                  </a>
                )}
              </td>
              <td className="py-2 pr-3 text-slate-700">{p.quantity ?? 1}</td>
              <td className={`py-2 pr-3 ${meta.className}`}>
                <span className="inline-flex items-center gap-1">
                  <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                  {meta.label}
                </span>
                {p.research_confidence !== null && p.research_confidence !== undefined && (
                  <div className="text-[10px] text-slate-500">
                    conf. {p.research_confidence}%
                  </div>
                )}
              </td>
              <td className="py-2 pr-3 text-right text-slate-800">
                {p.unit_price ? formatCurrency(p.unit_price, currency) : "—"}
              </td>
              <td className="py-2 pr-3 text-right font-semibold text-[#1E3A5F]">
                {p.line_total ? formatCurrency(p.line_total, currency) : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const formatCurrency = (amount, currency = "USD") => {
  if (amount === null || amount === undefined) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
};

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return raw;
  }
};

const RequestHistory = ({ refreshKey, user }) => {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const timerRef = useRef(null);
  const tickRef = useRef(0);

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      setRefreshing(true);
      try {
        const data = await priceRequestsApi.listMine();
        const raw =
          (Array.isArray(data) && data) ||
          data?.requests ||
          data?.items ||
          data?.data ||
          [];
        const list = raw.map((r) => ({
          id: r?.id || r?._id || r?.request_id || Math.random().toString(36),
          request_number: r?.request_number || r?.number || r?.code || "",
          status: r?.status || "pending",
          created_at: r?.createdAt || r?.created_at || r?.date || "",
          currency: r?.currency || "USD",
          total_estimated_price: r?.total_estimated_price ?? r?.total ?? r?.total_price ?? null,
          pdf_url: buildPdfUrl(r),
          summary:
            r?.summary ||
            (Array.isArray(r?.products)
              ? r.products
                  .map((p) => p?.description)
                  .filter(Boolean)
                  .slice(0, 2)
                  .join(" · ")
              : ""),
          products_count:
            r?.products_count ?? (Array.isArray(r?.products) ? r.products.length : 0),
          products: Array.isArray(r?.products) ? r.products : [],
        }));
        setItems(list);
        setError("");
        setStatus(
          `Loaded ${list.length} request${list.length === 1 ? "" : "s"} at ${new Date().toLocaleTimeString()}`
        );
      } catch (err) {
        setError(err.message || "Could not load your previous requests.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  // Initial load + on external refresh signal
  useEffect(() => {
    tickRef.current = 0;
    load();
  }, [load, refreshKey]);

  // Auto-refresh while any request is researching, capped to MAX_AUTO_REFRESHES.
  useEffect(() => {
    const anyResearching = items.some(
      (it) => (it.status || "").toLowerCase() === "researching"
    );
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (!anyResearching) return;
    timerRef.current = setInterval(() => {
      tickRef.current += 1;
      if (tickRef.current >= MAX_AUTO_REFRESHES) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setStatus("Auto-refresh paused. Tap Refresh to check again.");
        return;
      }
      load({ silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [items, load]);

  const handleManualRefresh = () => {
    tickRef.current = 0;
    load({ silent: true });
  };

  const generateQuotationPdf = (item) => {
    try {
      const doc = new jsPDF();
      const products = item.products || [];
      const currency = item.currency || "USD";
      const totalStr = formatCurrency(item.total_estimated_price, currency) || "—";

      // Header
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.setTextColor(20, 184, 166);
      doc.text("PriceValuator", 15, 18);

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("Product Quotation", 200, 15, { align: "right" });
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Request ${item.request_number || "—"}`, 200, 22, { align: "right" });
      doc.text(`Issued: ${formatDate(item.researched_at || item.updated_at || item.created_at)}`, 200, 27, { align: "right" });

      doc.setDrawColor(20, 184, 166);
      doc.setLineWidth(1);
      doc.line(15, 32, 195, 32);

      // Customer block
      let yPos = 40;
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.setFont(undefined, "bold");
      doc.text("Prepared for", 15, yPos);
      doc.setFont(undefined, "normal");
      const custLines = [
        user?.company || "",
        user?.username ? `Account: ${user.username}` : "",
        user?.email || "",
      ].filter(Boolean);
      custLines.forEach((line, i) => doc.text(line, 15, yPos + 6 + i * 5));

      // Status + total box (right)
      doc.setFillColor(209, 250, 229);
      doc.rect(120, yPos - 3, 75, 25, "F");
      doc.setDrawColor(16, 185, 129);
      doc.setLineWidth(0.5);
      doc.rect(120, yPos - 3, 75, 25, "S");
      doc.setFontSize(9);
      doc.setTextColor(6, 95, 70);
      doc.text("Estimated total", 125, yPos + 3);
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(totalStr, 125, yPos + 15);
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.text(`Status: ${(item.status || "").replace(/_/g, " ")}`, 125, yPos + 20);

      // Table
      const tableStartY = yPos + 32;
      const body = products.map((p, idx) => [
        String(p.quantity ?? 1),
        `${p.description || ""}${p.brand || p.model ? `\n${[p.brand, p.model].filter(Boolean).join(" · ")}` : ""}${p.research_source_url ? `\nSource: ${p.research_source_url}` : ""}`,
        p.research_source === "db"
          ? "Matched database"
          : p.research_source === "llm"
          ? "AI research"
          : p.research_source === "none"
          ? "Not researched"
          : "—",
        p.research_confidence != null ? `${p.research_confidence}%` : "—",
        p.unit_price != null ? formatCurrency(p.unit_price, currency) : "—",
        p.line_total != null ? formatCurrency(p.line_total, currency) : "—",
      ]);
      autoTable(doc, {
        startY: tableStartY,
        head: [["QTY", "PRODUCT", "SOURCE", "CONF.", "UNIT", "LINE TOTAL"]],
        body,
        foot: [["", "", "", "", "TOTAL", totalStr]],
        theme: "grid",
        headStyles: { fillColor: [243, 244, 246], textColor: [55, 65, 81], fontStyle: "bold", fontSize: 9 },
        footStyles: { fillColor: [243, 244, 246], textColor: [4, 120, 87], fontStyle: "bold", fontSize: 10 },
        styles: { font: "helvetica", fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 12, halign: "center" },
          1: { cellWidth: 78 },
          2: { cellWidth: 30 },
          3: { cellWidth: 16, halign: "right" },
          4: { cellWidth: 24, halign: "right" },
          5: { cellWidth: 24, halign: "right", textColor: [4, 120, 87], fontStyle: "bold" },
        },
      });

      // Notes / disclaimer
      const finalY = doc.lastAutoTable?.finalY || tableStartY;
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        "Prices are AI-researched fair-market estimates and may vary from final sourcing quotes.",
        15,
        finalY + 8
      );

      const filename = `Quotation_${item.request_number || "PR"}_${new Date()
        .toISOString()
        .split("T")[0]}.pdf`;
      doc.save(filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PDF generation error", err);
      alert("Could not generate the PDF. Please try again.");
    }
  };

  const canDownload = (it) =>
    !!it.total_estimated_price &&
    ["quoted", "requires_attention"].includes((it.status || "").toLowerCase());

  return (
    <section
      data-testid="request-history"
      aria-label="My previous quotation requests"
      className="rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <h3 className="flex items-center gap-2 text-base font-bold text-[#1E3A5F]">
          <Layers className="w-4 h-4" aria-hidden="true" />
          My previous requests
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleManualRefresh}
          disabled={loading || refreshing}
          data-testid="pr-history-refresh-btn"
          className="text-slate-700 hover:bg-slate-100 rounded-full"
        >
          {refreshing ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-1" />
          )}
          Refresh
        </Button>
      </header>

      <p className="sr-only" aria-live="polite" data-testid="pr-history-status">
        {status}
      </p>

      <div className="p-5">
        {error && (
          <Alert className="mb-4 bg-red-50 border-red-200">
            <AlertDescription className="text-red-800" data-testid="pr-history-error">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {loading && (
          <div className="text-center py-8 text-slate-600">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" aria-hidden="true" />
            Loading your requests...
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="text-center py-10 text-slate-500" data-testid="pr-history-empty">
            <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" aria-hidden="true" />
            You do not have previous quotation requests yet.
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="pr-history-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4">Request #</th>
                    <th className="py-2 pr-4">Products</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4 text-right">Est. total</th>
                    <th className="py-2 pr-4">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const totalStr = formatCurrency(it.total_estimated_price, it.currency);
                    const expanded = expandedId === it.id;
                    return (
                      <React.Fragment key={it.id}>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 pr-4 font-semibold text-[#1E3A5F]">
                            {it.request_number ? `#${it.request_number}` : "—"}
                          </td>
                          <td className="py-3 pr-4 text-slate-700">
                            {it.summary || "—"}
                            {it.products_count > 2 && (
                              <span className="text-xs text-slate-500">
                                {" "}+ {it.products_count - 2} more
                              </span>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            <StatusBadge status={it.status} />
                          </td>
                          <td className="py-3 pr-4 text-slate-600">{formatDate(it.created_at)}</td>
                          <td className="py-3 pr-4 text-right font-semibold text-[#1E3A5F]">
                            {totalStr || <span className="text-slate-400 text-xs font-normal">—</span>}
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setExpandedId(expanded ? null : it.id)}
                                data-testid={`pr-history-toggle-${it.id}`}
                                className="text-cyan-700 hover:text-cyan-900 text-xs font-semibold"
                                aria-expanded={expanded}
                              >
                                {expanded ? "Hide" : "View"} details
                              </button>
                              {it.pdf_url ? (
                                <a
                                  href={it.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`pr-history-pdf-${it.id}`}
                                  className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-xs font-semibold"
                                >
                                  <Download className="w-3.5 h-3.5" aria-hidden="true" />
                                  PDF
                                </a>
                              ) : canDownload(it) ? (
                                <button
                                  type="button"
                                  onClick={() => generateQuotationPdf(it)}
                                  data-testid={`pr-history-download-${it.id}`}
                                  className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-xs font-semibold"
                                >
                                  <FileDown className="w-3.5 h-3.5" aria-hidden="true" />
                                  Download PDF
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="bg-slate-50" data-testid={`pr-history-details-${it.id}`}>
                            <td colSpan={6} className="p-4">
                              <ProductBreakdown items={it.products} currency={it.currency} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-3" data-testid="pr-history-cards">
              {items.map((it) => {
                const totalStr = formatCurrency(it.total_estimated_price, it.currency);
                const expanded = expandedId === it.id;
                return (
                  <div
                    key={it.id}
                    className="rounded-xl border border-slate-200 p-4 space-y-2 bg-white"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#1E3A5F]">
                        {it.request_number ? `#${it.request_number}` : "—"}
                      </span>
                      <StatusBadge status={it.status} />
                    </div>
                    <p className="text-sm text-slate-700">{it.summary || "—"}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{formatDate(it.created_at)}</span>
                      <span className="font-semibold text-[#1E3A5F]">
                        {totalStr || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : it.id)}
                        className="text-cyan-700 text-xs font-semibold"
                        data-testid={`pr-history-toggle-mobile-${it.id}`}
                        aria-expanded={expanded}
                      >
                        {expanded ? "Hide" : "View"} details
                      </button>
                      {it.pdf_url ? (
                        <a
                          href={it.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-cyan-700 font-semibold text-xs"
                          data-testid={`pr-history-pdf-mobile-${it.id}`}
                        >
                          <Download className="w-3.5 h-3.5" aria-hidden="true" />
                          PDF
                        </a>
                      ) : canDownload(it) ? (
                        <button
                          type="button"
                          onClick={() => generateQuotationPdf(it)}
                          className="inline-flex items-center gap-1 text-cyan-700 font-semibold text-xs"
                          data-testid={`pr-history-download-mobile-${it.id}`}
                        >
                          <FileDown className="w-3.5 h-3.5" aria-hidden="true" />
                          Download PDF
                        </button>
                      ) : null}
                    </div>
                    {expanded && (
                      <div className="pt-2 border-t border-slate-100" data-testid={`pr-history-details-mobile-${it.id}`}>
                        <ProductBreakdown items={it.products} currency={it.currency} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default RequestHistory;
